import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Headphones, Play, Globe, Music, ArrowRight, BookOpen } from 'lucide-react';

const AudioBooks = () => {
  const audiobooks = [
    {
      title: 'Wealthy Canadian',
      language: 'English',
      flag: '🇨🇦',
      description: 'The complete English audiobook version of the bestselling Wealthy Canadian.',
      link: 'https://music.youtube.com/playlist?list=PLa8z_InZE8ERHv0Ecg5fhPy6MZaBw_eAt',
      color: 'amber',
    },
    {
      title: 'Wealthy Canadian',
      language: 'Hindi',
      flag: '🇨🇦',
      description: 'हिंदी में वेल्थी कनाडियन का संपूर्ण ऑडियोबुक संस्करण।',
      link: 'https://music.youtube.com/playlist?list=PLa8z_InZE8EQHTRXWbfjhfyM6evk92ffo',
      color: 'orange',
    },
    {
      title: 'Wealthy Canadian',
      language: 'Mandarin',
      flag: '🇨🇦',
      description: '《富裕的加拿大人》完整版普通话有声书。',
      link: 'https://music.youtube.com/playlist?list=PLa8z_InZE8EROQgN7mZsYGHSwM2yXHRDH',
      color: 'red',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="audiobooks-page">
      <Helmet>
        <title>Wealthy Canadian Audiobook Free | English Hindi Mandarin | YouTube Music | Canadian Investing Book | Harry Vadalkar</title>
        <meta name="description" content="Listen to Wealthy Canadian audiobook free on YouTube Music. The bestselling Canadian investing book available in English, Hindi, and Mandarin. Learn TFSA strategies, RRSP planning, stock market investing, real estate, and wealth building on the go. Perfect for commuters and busy professionals." />
        <meta name="keywords" content="Wealthy Canadian audiobook, Wealthy Canadian audiobook free, Canadian investing audiobook, free audiobook investing Canada, investing audiobook free, Hindi investing audiobook, Mandarin finance audiobook, Chinese investing book, TFSA audiobook, RRSP audiobook, wealth building audiobook, Harry Vadalkar audiobook, YouTube Music audiobook free, personal finance audiobook Canada, stock market audiobook, real estate investing audiobook, cryptocurrency audiobook, financial freedom audiobook, Canadian finance audiobook, multilingual investing book, audiobook for investors" />
        <link rel="canonical" href="https://harryvadalkar.ca/audiobooks" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://harryvadalkar.ca/audiobooks" />
        <meta property="og:title" content="Wealthy Canadian Audiobook Free | English Hindi Mandarin" />
        <meta property="og:description" content="Listen to Wealthy Canadian audiobook free on YouTube Music. Available in English, Hindi, and Mandarin." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Wealthy Canadian Audiobook Free" />
        <meta name="twitter:description" content="Listen to Wealthy Canadian audiobook free on YouTube Music. Available in English, Hindi, and Mandarin." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-900/20 to-[#020617]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full mb-6">
            <Headphones className="w-4 h-4" />
            <span className="font-medium">Listen Anywhere</span>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
            Audio <span className="text-amber-400">Books</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Listen to Wealthy Canadian in your preferred language. Available on YouTube Music for free.
          </p>
        </div>
      </section>

      {/* Audio Books Grid */}
      <section className="py-20" data-testid="audiobooks-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {audiobooks.map((book, index) => (
              <div
                key={index}
                className="glass rounded-2xl overflow-hidden hover:border-amber-500/30 transition-all duration-300 group"
              >
                <div className="relative aspect-square bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_e36899ae-c2fa-440f-bf36-c9469e111964/artifacts/tx8gw0to_Screen%20Shot%202026-02-09%20at%208.22.50%20AM.png"
                  alt={`Wealthy Canadian Audiobook - ${book.language} Version`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4">
                  <div className="flex items-center gap-2 bg-slate-900/80 backdrop-blur px-3 py-1 rounded-full">
                    <span className="text-xl">{book.flag}</span>
                    <span className="text-slate-300 text-sm">{book.language}</span>
                  </div>
                </div>
              </div>
                <div className="p-6">
                  <h3 className="font-heading text-2xl font-bold text-white mb-2">{book.title}</h3>
                  <p className="text-amber-400 font-medium mb-3">{book.language} Version</p>
                  <p className="text-slate-400 mb-6">{book.description}</p>
                  
                  <a
                    href={book.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`audiobook-${book.language.toLowerCase()}`}
                    className="inline-flex items-center justify-center w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6 py-4 rounded-xl transition-all"
                  >
                    <Music className="w-5 h-5 mr-2" />
                    Listen on YouTube Music
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" data-testid="audiobooks-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Prefer Reading? <span className="text-amber-400">Get the Book</span>
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            The original Wealthy Canadian book is available on Amazon.
          </p>
          <a
            href="https://www.amazon.ca/Wealthy-Canadian-hilarious-heartfelt-journey-ebook/dp/B0FDDBRKV7"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="audiobooks-buy-book"
            className="inline-flex items-center bg-amber-500 hover:bg-amber-600 text-black font-bold px-10 py-4 rounded-full transition-all shadow-lg hover:shadow-amber-500/30 text-lg"
          >
            Buy on Amazon
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default AudioBooks;